import L from 'leaflet'
import './leaflet.heat'
import 'leaflet-rotatedmarker'

L.Map.include({
  customControls: {},
  _determinant(matrix) {
    if (matrix.length === 1) {
      return matrix[0][0];
    }
    if (matrix.length === 2) {
      return (
        matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0]
      );
    }
    if (matrix.length === 3) {
      return (
        matrix[0][0] * (matrix[1][1] * matrix[2][2] - matrix[1][2] * matrix[2][1]) -
        matrix[0][1] * (matrix[1][0] * matrix[2][2] - matrix[1][2] * matrix[2][0]) +
        matrix[0][2] * (matrix[1][0] * matrix[2][1] - matrix[1][1] * matrix[2][0])
      );
    }
    throw new Error('Matrix size not supported');
  },
  _getCofactor(matrix, row, col) {
    const minor = matrix
      .filter((_, r) => r !== row)
      .map(row => row.filter((_, c) => c !== col));
    return this._determinant(minor);
  },
  _inverse(matrix) {
    const det = this._determinant(matrix);
    if (det === 0) throw new Error('Matrix is not invertible');

    const cofactors = matrix.map((row, r) =>
      row.map((_, c) => {
        const cofactor = this._getCofactor(matrix, r, c);
        return ((r + c) % 2 === 0 ? 1 : -1) * cofactor;
      })
    );

    const adjugate = cofactors[0].map((_, c) => cofactors.map(row => row[c]));

    return adjugate.map(row => row.map(value => value / det));
  },
  _getAspectRatio(worldWidth, worldHeight) {
    if (worldWidth > worldHeight) {
      return {
        worldWidth: 1,
        worldHeight: worldWidth / worldHeight
      }
    } else {
      return {
        worldWidth: worldHeight / worldWidth,
        worldHeight: 1
      }
    }
  },
  convertPointToGameCoord ({ x, y }, config) {
    const aspectRation = this._getAspectRatio(config.worldWidth, config.worldHeight)
    const width256WithAspect = 256 / aspectRation.worldWidth
    const height256WithAspect = 256 / aspectRation.worldHeight

    const widthMargin = (256 - width256WithAspect) / 2
    const heightMargin = (256 - height256WithAspect) / 2

    const xNorm = (x - widthMargin) / width256WithAspect
    const yNorm = (y + heightMargin) / (-1 * height256WithAspect)

    const inverseMatrix = this._inverse(config.matrix)
    const pointVector = [xNorm, yNorm, 1]

    const result = [
      inverseMatrix[0][0] * pointVector[0] + inverseMatrix[1][0] * pointVector[1] + inverseMatrix[2][0] * pointVector[2],
      inverseMatrix[0][1] * pointVector[0] + inverseMatrix[1][1] * pointVector[1] + inverseMatrix[2][1] * pointVector[2],
      inverseMatrix[0][2] * pointVector[0] + inverseMatrix[1][2] * pointVector[1] + inverseMatrix[2][2] * pointVector[2]
    ]

    return result
  },
  convertPointToLocalCoord ({ x, y }, config) {
    const matrix = config.matrix
    const pointVector = [x, y, 1]
    const aspectRation = this._getAspectRatio(config.worldWidth, config.worldHeight)

    const result = [
      matrix[0][0] * pointVector[0] + matrix[1][0] * pointVector[1] + matrix[2][0] * pointVector[2],
      matrix[0][1] * pointVector[0] + matrix[1][1] * pointVector[1] + matrix[2][1] * pointVector[2],
      matrix[0][2] * pointVector[0] + matrix[1][2] * pointVector[1] + matrix[2][2] * pointVector[2]
    ]

    const xNorm = result[0]
    const yNorm = result[1]

    const width256WithAspect = 256 / aspectRation.worldWidth
    const height256WithAspect = 256 / aspectRation.worldHeight

    const widthMargin = (256 - width256WithAspect) / 2
    const heightMargin = (256 - height256WithAspect) / 2

    const x256 = xNorm * width256WithAspect + widthMargin
    const y256 = yNorm * -1 * height256WithAspect - heightMargin

    return [x256, y256]
  },
  getLayerById: function (id) {
    for (const i in this._layers) {
      if (this._layers[i].id == id || this._layers[i]._leaflet_id == id) {
        return this._layers[i]
      }
    }
  },
  _initControlPos: function () {
    let corners = this._controlCorners = {},
        l = 'leaflet-',
        container = this._controlContainer =
          L.DomUtil.create('div', l + 'control-container', this._container);

    function createCorner(vSide, hSide) {
      let className = l + vSide + ' ' + l + hSide;

      corners[vSide + hSide] = L.DomUtil.create('div', className, container);
    }

    createCorner('top', 'left');
    createCorner('top', 'right');
    createCorner('top', 'center');
    createCorner('bottom', 'left');
    createCorner('bottom', 'right');
    createCorner('bottom', 'center');
  },
  _resizeControls () {
    if (!this._controlCorners || !this._size) return
    let currentHeight = 0
    const mapHeight = this._size.y - 10
    const controls = this._controlCorners.topleft.childNodes
    const bigControls = []

    controls.forEach(control => {
      control.style.removeProperty("height")
      if (mapHeight + currentHeight + 10 > control.scrollHeight * 2) {
        currentHeight = currentHeight + control.scrollHeight + 10
      } else {
        bigControls.push(control)
      }
    })

    bigControls.forEach(control => {
      const totalHeight = mapHeight - currentHeight
      control.style.height = ((totalHeight - 10 * bigControls.length) / bigControls.length) + 'px'
    })
  },
  removeControl: function (control) {
    control.remove()
    if (control.id) {
      delete this.customControls[control.id]
    }
    return this
  },
});

L.LayerGroup.include({
  getLayerById: function (id) {
    for (const i in this._layers) {
      if (this._layers[i].id == id || this._layers[i]._leaflet_id == id) {
        return this._layers[i];
      }
    }
  }
})

L.Marker.include({
  setInteractive: function (interactive) {
    this.options.interactive = interactive;
    if (this._map) {
      this._updateInteraction();
    }

    return this;
  },
  _updateInteraction: function () {
    if (this.options.interactive) {
      L.DomUtil.addClass(this._icon, 'leaflet-interactive');
      this.addInteractiveTarget(this._icon);

      if (this.options.draggable && this.dragging) {
        this.dragging.enable()
      }
    } else {
      this.removeInteractiveTarget(this._icon);

      L.DomUtil.removeClass(this._icon, 'leaflet-interactive');

      if (this.dragging) {
        this.dragging.disable()
      }
    }
  }
})

export default L

